/**
 * ThisDevicePanel — offline readiness for the computer the operator is using.
 *
 * Split from DevicesPanel under TCA-26100804: that page is named for the shop's
 * registered computers, and this content describes only the local machine. The
 * sections are deliberately separate because readiness data exists only for the
 * current device — no other device's readiness is visible from here.
 *
 * Slice 1 preserves the existing cards and controls verbatim so nothing becomes
 * unreachable between slices. Slice 2 replaces this stack with the tabbed
 * status model.
 */
import React, { useState, useEffect, useCallback } from 'react';
import useDesktopScope from '@/hooks/useDesktopScope';
import useDesktopObservation from '@/hooks/useDesktopObservation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Map } from 'lucide-react';
import { toast } from 'sonner';

import {
  getDesktopOfflineMapUpdateStatus,
  installDesktopOfflineMap,
  rollbackDesktopOfflineMap,
} from '@/api/offlineMap';
import {
  getRoutingDataUpdateStatus,
  installRoutingData,
  rollbackRoutingData,
} from '@/api/offlineRoutingAssets';
import { getPublicShopBranding } from '@/api/shop';
import { useOfflineAssetControls } from '@/hooks/useOfflineAssetControls';
import useMediaReadinessStatus from '@/hooks/useMediaReadinessStatus';
import useCatalogMediaReadinessStatus from '@/hooks/useCatalogMediaReadinessStatus';
import { buildThisDeviceModel, THIS_DEVICE_TABS } from '@/utils/thisDeviceModel';
import ProtectedStoreRecovery from './ProtectedStoreRecovery';
import ThisDeviceMapsTab from './ThisDeviceMapsTab';
import OrderDataCard from './OrderDataCard';
import LocalRuntimeCard from './LocalRuntimeCard';
import { StatusOverriddenProvider } from './DeviceStatusSlot';
import ThisDeviceMediaTab from './ThisDeviceMediaTab';
import ThisDeviceOlderOrdersTab from './ThisDeviceOlderOrdersTab';
import ThisDeviceCatalogTab from './ThisDeviceCatalogTab';

const TONE_TEXT = Object.freeze({
  green: 'text-emerald-700',
  amber: 'text-amber-700',
  red: 'text-rose-700',
  quiet: 'text-stone-500',
});

const OFFLINE_MAP_STATUS_LABELS = {
  desktop_unavailable: 'Available in the desktop app',
  missing_local: 'No offline map installed',
  current: 'Map package up to date',
  update_available: 'Map update available',
  published_unavailable: 'Map update status unavailable',
  invalid_local_manifest: 'Installed map record is invalid',
  invalid_published_manifest: 'Published map record is invalid',
};
const OFFLINE_MAP_STATUS_DETAIL = {
  desktop_unavailable: 'Open this settings page in the desktop app to check map assets.',
  missing_local: 'Map-based offline dispatch is unavailable until the offline map is installed.',
  current: 'Current map package is up to date. No new version is available.',
  update_available: 'A newer offline map is published and ready to install.',
  published_unavailable: 'New-version checking is unavailable. Installed details below reflect the local observation.',
  invalid_local_manifest: 'The local manifest or PMTiles file needs repair before map updates can run.',
  invalid_published_manifest: 'The published manifest is not usable. Download actions are disabled.',
};
const OFFLINE_MAP_LABELS = {
  installed: 'Offline map installed',
  restored: 'Previous offline map restored',
  rollbackConfirm:
    'Restore previous map package? This will replace the current offline map '
    + 'with the previously installed version.',
};

const ROUTING_DATA_LABELS = {
  rollbackConfirm:
    'Restore previous routing data? This will replace the current routing data '
    + 'with the previously installed version.',
};

const ThisDevicePanel = () => {
  const [shopTimezone, setShopTimezone] = useState(null);
  const [activeTab, setActiveTab] = useState(THIS_DEVICE_TABS[0].id);
  // Reported by the Older orders tab once its status is read; null until then,
  // so the navigation never presents an unread count as a confirmed zero.
  const [keptRanges, setKeptRanges] = useState(null);
  const [catalogOwn, setCatalogOwn] = useState(null);
  const runtime = useDesktopObservation('getLocalRuntimeStatus', 'onLocalRuntimeStatusChanged');
  const runtimeStatus = runtime.status;
  const { status: mediaStatus, loading: mediaLoading } = useMediaReadinessStatus();
  const { status: catalogStatus } = useCatalogMediaReadinessStatus();

  // Arrow-key movement across the tab list, as the tablist role requires.
  const onTabKeyDown = useCallback((event, index) => {
    const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    const step = keys[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + THIS_DEVICE_TABS.length) % THIS_DEVICE_TABS.length;
    setActiveTab(THIS_DEVICE_TABS[next].id);
    document.getElementById(`this-device-tab-${THIS_DEVICE_TABS[next].id}`)?.focus();
  }, []);
  const {
    status: offlineMap, phase: mapPhase, busy: mapBusy, outcome: mapOutcome,
    loading: offlineMapLoading,
    installing: offlineMapInstalling,
    rollingBack: offlineMapRollingBack,
    refresh: refreshOfflineMap,
    onInstall: handleInstallOfflineMap,
    onRollback: handleRollbackOfflineMap,
  } = useOfflineAssetControls({
    kind: 'map', runtime, fetchStatus: getDesktopOfflineMapUpdateStatus,
    install: installDesktopOfflineMap,
    rollback: rollbackDesktopOfflineMap,
    labels: OFFLINE_MAP_LABELS,
  });

  const {
    status: routingData, phase: routingPhase, busy: routingBusy, outcome: routingOutcome,
    loading: routingDataLoading,
    installing: routingDataInstalling,
    rollingBack: routingDataRollingBack,
    refresh: refreshRoutingData,
    onInstall: handleInstallRoutingData,
    onRollback: handleRollbackRoutingData,
  } = useOfflineAssetControls({
    kind: 'routing', runtime, fetchStatus: getRoutingDataUpdateStatus,
    install: installRoutingData,
    rollback: rollbackRoutingData,
    labels: ROUTING_DATA_LABELS,
  });
  useEffect(() => {
    let active = true;
    setShopTimezone(null);
    setKeptRanges(null);
    setCatalogOwn(null);
    getPublicShopBranding().then(value => {
      if (active && runtime.scope.current() && value?.timezone) setShopTimezone(value.timezone);
    }).catch(() => {});
    return () => { active = false; };
  }, [runtime.scope]);

  const canInstallOfflineMap = ['missing_local', 'update_available'].includes(
    offlineMap?.status,
  );
  const canRollbackOfflineMap = Boolean(offlineMap?.local?.rollbackAvailable);

  // Show the button only when it does something, as the routing card already
  // does. The old disabled "Map action" named no action in five of the seven
  // states; the status line explains those instead (TCA-26100804).
  const offlineMapActionLabel = () => {
    if (offlineMapInstalling) return 'Installing…';
    if (offlineMap?.status === 'update_available') return 'Update map';
    if (offlineMap?.status === 'missing_local') return 'Download map';
    return null;
  };

  const model = buildThisDeviceModel({
    runtime: runtimeStatus,
    media: mediaStatus,
    mediaLoading,
    catalog: catalogStatus,
    catalogOwn,
    map: offlineMap, mapPhase, routingPhase,
    routing: routingData,
    historicalRangeCount: keptRanges ? keptRanges.rangeCount : null,
  });
  const { override } = model;
  // The override is not an extra message: it replaces each tab's own status and
  // fences the actions that need the services it says are down. The
  // protected-store recovery controls are deliberately not fenced — they are
  // the route out of this state (TCA-26100804).
  const servicesUnavailable = runtime.phase !== 'ready' || runtimeStatus?.state !== 'ready';

  const panels = {
    overview: (
      <>
        <ProtectedStoreRecovery />
        <LocalRuntimeCard shopTimezone={shopTimezone} runtime={runtime} />
      </>
    ),
    'order-data': <OrderDataCard shopTimezone={shopTimezone} />,
    media: (
      <ThisDeviceMediaTab
        status={mediaStatus}
        loading={mediaLoading}
        shopTimezone={shopTimezone}
      />
    ),
    catalog: (
      <ThisDeviceCatalogTab
        referenceImages={catalogStatus}
        shopTimezone={shopTimezone}
        onSummary={setCatalogOwn}
      />
    ),
    maps: (
      <ThisDeviceMapsTab
        map={offlineMap}
        mapPhase={mapPhase} mapBusy={mapBusy} mapOutcome={mapOutcome} onRefreshMap={refreshOfflineMap}
        mapLoading={offlineMapLoading}
        mapInstalling={offlineMapInstalling}
        mapRollingBack={offlineMapRollingBack}
        onInstallMap={handleInstallOfflineMap}
        onRollbackMap={handleRollbackOfflineMap}
        mapActionLabel={offlineMapActionLabel()}
        canInstallMap={canInstallOfflineMap}
        canRollbackMap={canRollbackOfflineMap}
        statusLabels={OFFLINE_MAP_STATUS_LABELS}
        statusDetails={OFFLINE_MAP_STATUS_DETAIL}
        routing={routingData}
        routingPhase={routingPhase} routingBusy={routingBusy} routingOutcome={routingOutcome} onRefreshRouting={refreshRoutingData}
        routingLoading={routingDataLoading}
        routingInstalling={routingDataInstalling}
        routingRollingBack={routingDataRollingBack}
        onInstallRouting={handleInstallRoutingData}
        onRollbackRouting={handleRollbackRoutingData}
        servicesUnavailable={servicesUnavailable}
      />
    ),
    'older-orders': <ThisDeviceOlderOrdersTab onSummary={setKeptRanges} />,
  };

  return (
    <div className="flex gap-6" data-testid="this-device-panel">
      <div
        className="w-56 flex-shrink-0 flex flex-col gap-1"
        role="tablist"
        aria-orientation="vertical"
        aria-label="This device"
      >
        {THIS_DEVICE_TABS.map((tab, index) => {
          const view = model.tabs[tab.id];
          const selected = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`this-device-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`this-device-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onKeyDown={(event) => onTabKeyDown(event, index)}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-xl px-3 py-2 text-left transition-colors ${
                selected ? 'bg-stone-100' : 'hover:bg-stone-50'
              }`}
              data-testid={`this-device-tab-${tab.id}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-stone-800">{tab.label}</span>
                {view.attention && (
                  <>
                    <span
                      aria-hidden="true"
                      className={`h-2 w-2 rounded-full ${view.tone === 'red' ? 'bg-rose-600' : 'bg-amber-500'}`}
                      data-testid={`this-device-attention-${tab.id}`}
                    />
                    <span className="sr-only">needs attention</span>
                  </>
                )}
              </span>
              {view.summary && (
                <span className={`block text-xs mt-0.5 ${TONE_TEXT[view.tone] || TONE_TEXT.quiet}`}>
                  {view.summary}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div
        className="flex-1 min-w-0 space-y-4"
        role="tabpanel"
        id={`this-device-panel-${activeTab}`}
        aria-labelledby={`this-device-tab-${activeTab}`}
      >
        <div aria-live={override ? 'assertive' : 'polite'}>
          {override && (
            <div
              className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-3"
              data-testid="this-device-page-problem"
            >
              <p className="text-sm font-semibold text-rose-900">{override.label}</p>
              <p className="text-xs text-rose-800 mt-1">{override.detail}</p>
            </div>
          )}
        </div>
        <StatusOverriddenProvider key={`${runtime.scope.revision}:${runtime.scope.identity}`} overridden={servicesUnavailable}>
          {panels[activeTab]}
        </StatusOverriddenProvider>
      </div>
    </div>
  );
};

export default function ThisDeviceOpening() {
  const scope = useDesktopScope();
  return <ThisDevicePanel key={scope.id} />;
}
